"use client";

import { useState } from "react";
import Link from "next/link";
import { ChevronDown, CreditCard, User, Lock, Upload, Radio, Shield, AlertTriangle, Settings } from "lucide-react";

const SECTIONS = [
  {
    title: "Getting Started",
    icon: User,
    items: [
      { q: "How do I create an account?", a: "Tap \"Sign up\" in the top right corner. Enter your email, choose a username, set a password, and select whether you want a Viewer or Creator account. Viewer accounts can browse and purchase content. Creator accounts require admin approval before you can upload." },
      { q: "What is the difference between a Viewer and a Creator?", a: "Viewers browse content, subscribe to creators, tip, and unlock pay-per-view content using Credits. Creators can do everything Viewers can, plus upload their own content, set subscription prices, go live, and earn Credits from their audience." },
      { q: "How do I upgrade from Viewer to Creator?", a: "Go to Settings > Account and look for the \"Become a Creator\" section. Submit a request with a brief explanation of what content you plan to create. An admin will review and approve or deny your request. You will be notified once a decision is made." },
      { q: "How do I edit my profile?", a: "Go to Settings > Profile. You can change your username, bio, avatar (click the photo to upload a new one), and banner image. Creators have an additional tab for setting their tagline, creator bio, and subscription price." },
    ],
  },
  {
    title: "Credits and Payments",
    icon: CreditCard,
    items: [
      { q: "What are Credits?", a: "Credits (also called Commissary) are the virtual currency used on CellBloxk Barbie. You purchase Credits with real money via Stripe, then spend them on subscriptions, tips, and pay-per-view content." },
      { q: "How do I buy Credits?", a: "Click the green credit balance in the header or go to the Commissary page. Choose a credit package (Starter through Whale), then complete checkout via Stripe. Credits are added to your account instantly after payment." },
      { q: "Are Credits refundable?", a: "Credits are non-refundable once purchased, except as required by applicable law. Credits spent on subscriptions, tips, and unlocks cannot be reversed." },
      { q: "How do Creators earn money?", a: "Creators earn Credits when users subscribe to them, tip them, or unlock their pay-per-view content. The platform takes a 20% fee on all transactions. Creators keep the remaining 80%. Payout details are available in the Creator Dashboard." },
      { q: "What payment methods are accepted?", a: "We accept all major credit and debit cards through Stripe, including Visa, Mastercard, American Express, and Discover." },
    ],
  },
  {
    title: "Subscriptions and Content",
    icon: Lock,
    items: [
      { q: "How do subscriptions work?", a: "Each Creator sets their own monthly subscription price in Credits. When you subscribe, the Credits are deducted from your balance and you get access to all of that Creator's subscriber-only content for 30 days. Subscriptions do not auto-renew -- you manually renew when they expire." },
      { q: "What is pay-per-view (PPV) content?", a: "Some content is priced individually instead of being included in a subscription. You pay a one-time Credit cost to unlock it permanently. If you are subscribed to the Creator, you get access to their PPV content as part of your subscription." },
      { q: "Can I download content?", a: "No. All content is stream-only and cannot be downloaded. Downloading, screen recording, or redistributing content is a violation of our Terms of Service and may result in account termination." },
    ],
  },
  {
    title: "Creator Tools",
    icon: Upload,
    items: [
      { q: "How do I upload content?", a: "From the Creator Dashboard, click \"Upload Content.\" Fill in the title, description, choose the access level (Free, Subscribers Only, or PPV), set a price if PPV, paste the video/image URL, add a thumbnail, select a category, and add tags. Click Publish." },
      { q: "How do I set my subscription price?", a: "Go to Settings > Creator tab. Enter your desired monthly price in Credits and click Save. You can also use the quick-set buttons for common price points." },
      { q: "How do I go live?", a: "From the sidebar menu, tap \"Go Live\" or navigate to /creator/live. Enter a session title, optional description, choose whether it is subscribers-only, then hit \"Go Live.\" Your viewers will see your session on the Live page and can chat with you in real time." },
    ],
  },
  {
    title: "Live Sessions",
    icon: Radio,
    items: [
      { q: "How do I watch a live session?", a: "Go to the Live page from the navigation menu. You will see all currently active and scheduled sessions. Tap any session to join. Some sessions may be restricted to subscribers of that Creator." },
      { q: "How does live chat work?", a: "When watching a live session, the chat panel appears alongside the stream. Type a message and hit send. You can also tip the Creator directly in chat by clicking the dollar sign button, selecting an amount, and sending your tip with an optional message." },
      { q: "Can I tip during a live session?", a: "Yes. In the live chat, tap the dollar sign icon to enter tip mode. Select a preset amount or enter a custom amount, type an optional message, and send. The Creator receives 80% of the tip. Tip messages are highlighted in the chat." },
    ],
  },
  {
    title: "Account and Security",
    icon: Settings,
    items: [
      { q: "How do I change my password?", a: "Currently, password changes are handled through our support team. Contact us at support@cellbloxkbarbie.com with your account email to request a password reset." },
      { q: "How do I delete my account?", a: "Go to Settings > Account and scroll to the Danger Zone at the bottom. Click Delete Account. This action is permanent and cannot be undone. Any remaining Credits will be forfeited." },
      { q: "What if someone is using my content without permission?", a: "File a DMCA takedown request. Go to our DMCA page for full instructions, or email dmca@cellbloxkbarbie.com with the URLs of the infringing content and proof of your ownership." },
    ],
  },
];

export default function HelpCenterPage() {
  const [openItems, setOpenItems] = useState<Record<string, boolean>>({});

  const toggle = (key: string) => setOpenItems((prev) => ({ ...prev, [key]: !prev[key] }));

  return (
    <main className="min-h-screen bg-[#050505] text-neutral-300">
      <div className="max-w-3xl mx-auto px-4 py-12">
        <h1 className="text-2xl font-black text-white uppercase tracking-tight mb-2">Help Center</h1>
        <p className="text-sm text-neutral-500 mb-10">Find answers to common questions about using CellBloxk Barbie.</p>

        <div className="space-y-8">
          {SECTIONS.map((section) => (
            <div key={section.title}>
              <div className="flex items-center gap-2.5 mb-4">
                <section.icon className="w-4 h-4 text-red-500" />
                <h2 className="text-base font-bold text-white uppercase tracking-wider">{section.title}</h2>
              </div>
              <div className="space-y-1">
                {section.items.map((item, i) => {
                  const key = `${section.title}-${i}`;
                  const isOpen = openItems[key];
                  return (
                    <div key={key} className="border border-neutral-800/60 rounded-lg overflow-hidden">
                      <button
                        onClick={() => toggle(key)}
                        className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-white/[0.02] transition"
                      >
                        <span className="text-sm font-medium text-neutral-200">{item.q}</span>
                        <ChevronDown className={`w-4 h-4 text-neutral-500 shrink-0 ml-4 transition-transform ${isOpen ? "rotate-180" : ""}`} />
                      </button>
                      {isOpen && (
                        <div className="px-4 pb-4 pt-1 text-sm text-neutral-400 leading-relaxed border-t border-neutral-800/40">
                          {item.a}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>

        <div className="mt-12 p-6 bg-[#0a0a0a] border border-neutral-800/60 rounded-lg text-center">
          <p className="text-sm text-neutral-400 mb-3">Still need help?</p>
          <Link href="/support/contact" className="inline-block bg-red-600 hover:bg-red-500 text-white px-6 py-2.5 rounded-lg font-bold uppercase text-[11px] tracking-wider transition">Contact Support</Link>
        </div>
      </div>
    </main>
  );
}
